import { user as userTools, money } from '@sellpy/commons'
import memoize from 'lodash/memoize'
import { DataLayer } from './dataLayer'

export const getTileTrackFunction = memoize(
  (data) => () => analyticsSliderTileClicked(data),
  ({ sliderId, tileId, index, totalCount }) => `${sliderId}_${tileId}_${index}_${totalCount}`
)

export function analyticsEnableAnalytics() {
  DataLayer.trackPermissionAnalytics({ permissionValue: true })
}

export function analyticsDisableAnalytics() {
  DataLayer.trackPermissionAnalytics({ permissionValue: false })
}

export function analyticsEnableExternalMarketing() {
  DataLayer.trackPermissionExternalMarketing({ permissionValue: true })
}

export function analyticsDisableExternalMarketing() {
  DataLayer.trackPermissionExternalMarketing({ permissionValue: false })
}

export function analyticsEnableEmailMarketing() {
  DataLayer.trackPermissionEmailMarketing({ permissionValue: true })
}

export function analyticsDisableEmailMarketing() {
  DataLayer.trackPermissionEmailMarketing({ permissionValue: false })
}

export function analyticsSetUserId(userId) {
  DataLayer.trackSetUserId({ id: userId })
}

export function analyticsUnsetUserId() {
  DataLayer.trackUnsetUserId()
}

export const analyticsSetUserEmail = ({ email }) => {
  email && DataLayer.trackSetUserEmail({ email })
}

export function analyticsUnsetUserEmail() {
  DataLayer.trackUnsetUserEmail()
}

export const analyticsSetUserPhone = ({ phone }) => {
  phone && DataLayer.trackSetUserPhone({ phone })
}

export function analyticsUnsetUserPhone() {
  DataLayer.trackUnsetUserPhone()
}

export const analyticsCheckoutAddPromoCode = (payload) =>
  DataLayer.trackCheckoutAddPromoCode(payload)

export const analyticsCheckoutViewAddShipping = (payload) =>
  DataLayer.trackCheckoutViewAddShipping(payload)

export const analyticsCheckoutAddShippingInfo = (payload) =>
  DataLayer.trackCheckoutAddShippingInfo(payload)

export const analyticsCheckoutViewPaymentInfo = (payload) =>
  DataLayer.trackCheckoutViewPaymentInfo(payload)

export const analyticsCheckoutAddPaymentInfo = (payload) =>
  DataLayer.trackCheckoutAddPaymentInfo(payload)

export const analyticsCreditConversion = (payload) => DataLayer.trackCreditConversion(payload)

export const analyticsOptInAutomaticTransfers = (payload) =>
  DataLayer.trackOptInAutomaticTransfers(payload)

export const analyticsAutomaticPayoutSettings = (payload) =>
  DataLayer.trackAutomaticPayoutSettings(payload)

export const analyticsViewCreditConversion = (payload) =>
  DataLayer.trackViewCreditConversion(payload)

export const analyticsViewAddEmail = (payload) => DataLayer.trackCheckoutViewAddEmail(payload)

export const analyticsDiscoverMore = (payload) => DataLayer.trackDiscoverMoreClicks(payload)

export const analyticsAddEmailInfo = (payload) => DataLayer.trackCheckoutAddEmailInfo(payload)

export const analyticsCheckoutViewAddAddress = (payload) =>
  DataLayer.trackCheckoutViewAddAddress(payload)

export const analyticsCheckoutAddShippingAddress = (payload) =>
  DataLayer.trackCheckoutAddShippingAddress(payload)

export const analyticsViewReviewOrder = (payload) => DataLayer.trackCheckoutViewReviewOrder(payload)

export const analyticsReviewOrder = (payload) => DataLayer.trackCheckoutReviewOrder(payload)

export const analyticsBeginCheckoutV2 = (payload) => DataLayer.trackBeginCheckoutV2(payload)

export function analyticsGiftCardPurchase({ totalPayment, paymentId }) {
  const amount = totalPayment.amount
  const currency = totalPayment.currency

  DataLayer.trackPurchase({
    purchaseType: 'giftCard',
    paymentId,
    price: amount,
    currency,
    stringValue: amount.toString()
  })
}

export function analyticsLoyaltyPeriodPurchase({ payment, paymentId }) {
  const amount = payment.amount
  const currency = payment.currency

  DataLayer.trackPurchase({
    purchaseType: 'loyaltyProgram',
    paymentId,
    price: amount,
    currency,
    stringValue: amount.toString()
  })
}

export const analyticsPurchaseV2 = (payload) => DataLayer.trackPurchaseV2(payload)

export const analyticsGiftCardAddedToCart = ({ amount, currency }) => {
  DataLayer.trackGiftCardAddToCart({ amount, currency })
}

export const analyticsGiftCardRemovedFromCart = ({ amount, currency }) => {
  DataLayer.trackGiftCardRemoveFromCart({ amount, currency })
}

export function analyticsMenuNavigation({ label, action }) {
  DataLayer.trackMenuNavigation({ label, action })
}

export function analyticsHomeTabNavigation({ tab }) {
  DataLayer.trackHomeTabNavigation({ tab })
}

export function analyticsMapDrawer({ action, pickupAvailable }) {
  DataLayer.trackMapDrawer({ action, pickupAvailable })
}

export function analyticsOrderPickupButton({ location }) {
  DataLayer.trackOrderPickupButton({ location })
}

export function analyticsDropoffButton() {
  DataLayer.trackDropoffButton()
}

export function analyticsCommencePreparedItemSale() {
  DataLayer.trackCommencePreparedItemSale()
}

export function analyticsTrackExtendPricingPeriodModalOpen() {
  DataLayer.trackExtendPricingPeriodModalOpen()
}

export function analyticsSetNewPricingModalOpen() {
  DataLayer.trackSetNewPricingModalOpen()
}

export function analyticsBagOrderButton({ location }) {
  DataLayer.trackBagOrderButton({ location })
}

export function analyticsContainerOrderHistoryButton({ location }) {
  DataLayer.trackContainerOrderHistoryButton({ location })
}

export function analyticsPlpPromotionalBanner({ action, name }) {
  DataLayer.trackPlpPromotionalTile({ action, name })
}

export const analyticsItemPricingChargeInitiated = ({ pricing: { amount }, itemId }) => {
  DataLayer.trackBeginCheckout({ items: [{ name: 'item_pricing', id: itemId, price: amount }] })
}

export const analyticsItemPricingChargeCompleted = ({ pricing: { amount }, itemId }) => {
  DataLayer.trackPurchase({ items: [{ name: 'item_pricing', id: itemId, price: amount }] })
}

export const analyticsGiftCardRedeemed = ({ giftCardId }) => {
  DataLayer.trackGiftCardRedeemed({ id: giftCardId })
}

export function analyticsReevaluationRequestStarted(data) {
  DataLayer.trackReevaluationRequestStart(data)
}

export function analyticsReevaluationRequestCompleted(data) {
  DataLayer.trackReevaluationRequestComplete(data)
  const { itemId, motivation, returnIfRejected } = data
  const eventType = 'ReevaluationRequestCompleted'
  intercomSend(eventType, { itemId, motivation, returnIfRejected })
}

export function analyticsSendBackRequestStarted(data) {
  DataLayer.trackSendBackRequestStart(data)
}

export function analyticsSendBackRequestCompleted(data) {
  DataLayer.trackSendBackRequestCompleted(data)
}

export function analyticsCheckout({ items }) {
  DataLayer.trackBeginCheckout({ items })
  intercomSend('InitiateCheckout')
}

export function analyticsSendBackRequestCheckoutInitiated(payment, properties = {}) {
  const amounts = [
    payment.get('adyenAmount') && money.toBaseUnit(payment.get('adyenAmount')),
    payment.get('creditAmount')
  ].filter(Boolean)
  DataLayer.trackBeginCheckout({
    items: [{ name: 'send_back_request', id: properties.itemId, price: money.add(...amounts) }]
  })
}

export function analyticsSendBackRequestCheckoutConfirmed(payment, amount) {
  DataLayer.trackPurchase({
    paymentId: payment.id,
    items: [{ name: 'send_back_request', price: amount }]
  })
}

export function analyticsRemoveFromCart(items, checkoutType) {
  DataLayer.trackRemoveFromCart({ checkoutType, items })
}

export function analyticsCart(items, checkoutType, user) {
  DataLayer.trackAddToCart({ checkoutType, items })
  trackEventAllProviders('AddToCart', {
    anonymous: !user || userTools.userIsAnonymous(user)
  })
}

export function analyticsAddToWishlist(items, checkoutType) {
  DataLayer.trackAddToWishList({ items, checkoutType })
  const eventType = 'AddToWishlist'
  intercomSend(eventType)
}

export function analyticsRemoveFromWishlist(items, checkoutType) {
  DataLayer.trackRemoveFromWishList({ items, checkoutType })
}

/**
 * Tracking for the autocomplete suggestions and serachbox
 *
 * @param {string} searchTerm The query that the customer has wrote
 * @param {string} searchSource There are four type of search facets / indexs depending which recommendation the customer clicked on. Either category, brand, suggestedText (querysuggestions), text (plain search).
 */
export function analyticsSearch({ searchTerm, searchSource }) {
  DataLayer.trackSearch({ searchTerm, searchSource })
}

export function analyticsAddTrackedSearch({ trackedSearch }) {
  DataLayer.trackAddTrackedSearch({
    name: trackedSearch.name || trackedSearch.attributes?.name || ''
  })
}

export function analyticsLogin(provider) {
  DataLayer.trackLogin({ method: provider })
  intercomSend('Login')
}

export function analyticsSignUp(provider) {
  DataLayer.trackSignup({ method: provider })
  intercomSend('SignUp')
}

export function analyticsPickUpOrder(nrOfBags, page) {
  DataLayer.trackPickupOrder({
    nrOfBags,
    page,
    title: 'Order Pickup'
  })
  intercomSend('BagPickUp')
}

export function analyticsBagRating(value, comment, improvementArea) {
  DataLayer.trackBagRating({ score: value, comment, improvementArea })
  // must have unique types, since one cannot filter on metadata in intercom
  intercomSend('BagRating' + value, { comment, improvementArea })
}

export function analyticsClaimCompleted({ itemId, paymentId }) {
  DataLayer.trackRefund({
    itemId,
    paymentId
  })
  intercomSend('ClaimCompleted', { itemId })
}

export const analyticsEditedItem = (itemId) => intercomSend('EditedItem', { itemId })

export const analyticsItemChangeRequested = (itemId) =>
  intercomSend('ItemChangeRequested', { itemId })

export const analyticsOrderRating = (orderId, score, improvementArea) =>
  intercomSend(`OrderRating-${score}`, { orderId, improvementArea })

export const analyticsSliderItemClicked = (data) => {
  DataLayer.trackClickSliderItem(data)
}

export const analyticsSliderTileClicked = ({ sliderId, tileId, ...data }) => {
  DataLayer.trackClickTile({
    baseCategory: sliderId,
    tileName: tileId,
    tile_id: tileId,
    ...data
  })
}

export const analyticsP2pStartSaleInitiated = (data) => {
  DataLayer.trackP2pSaleInitiated({ itemId: data.itemId, value: data.p2pListings })
}

export const analyticsP2pStartSaleCompleted = (data) => {
  DataLayer.trackP2pSaleCompleted({ itemId: data.itemId, value: data.p2pListings })
}

export const trackClickHeroBanner = ({ segment, campaign, position, target, ...data }) => {
  DataLayer.trackPromotionClick({
    promotionName: campaign,
    segment: segment,
    location: 'herobanner',
    position,
    target,
    ...data
  })
}

export const trackClickAsymmetricBanner = ({ segment, campaign, position }) => {
  DataLayer.trackPromotionClick({
    promotionName: campaign,
    segment: segment,
    location: 'asymmetricBanner',
    position
  })
}

export const trackClickStoreBanner = ({ segment, campaign, position }) => {
  DataLayer.trackPromotionClick({
    promotionName: campaign,
    segment: segment,
    location: 'storeBanner',
    position
  })
}

export const trackClickSalesBanner = ({ segment, campaign, position }) => {
  DataLayer.trackPromotionClick({
    promotionName: campaign,
    segment: segment,
    location: 'salesBanner',
    position
  })
}

const trackEventAllProviders = (eventType) => {
  intercomSend(eventType)
}

const intercomSend = (type, fields) => {
  setTimeout(() => window.Intercom && window.Intercom('trackEvent', type, fields), 3000)
}

export const analyticsSellpyFirstSignupInCart = () => {
  DataLayer.trackSellpyFirstCheckoutSignup()
}

export const analyticsItemPricingSelectedLength = ({ days, daysRemaining, price }) => {
  DataLayer.trackItemPricingSelectedLength({ days, daysRemaining, price })
}

export const analyticsCreateSearchProfile = ({ clickSource, type }) => {
  DataLayer.trackCreateSearchProfile({ clickSource, type })
}

export const analyticsCompleteSearchProfile = ({ clickSource, type } = {}) => {
  DataLayer.trackCompleteSearchProfile({ clickSource, type })
}

export const analyticsApplySearchProfile = ({ clickSource, type }) => {
  DataLayer.trackApplySearchProfile({ clickSource, type })
}

export const analyticsSuggestedBrandsBannerClick = (brand) => {
  DataLayer.trackSimilarBrandsBannerClick({ brand })
}

export const analyticsFulfillmentP2p = ({ action }) => {
  DataLayer.trackFulfillmentP2p({ action })
}

export function analyticsShowLoggedOutFavoriteModal() {
  DataLayer.trackViewFavoriteModal()
}

export function analyticsShowFilterNudgePopover() {
  DataLayer.trackViewFilterNudgePopover()
}

export const analyticsItemEditSubmit = ({
  itemId,
  dirtyFields,
  originalMetadata,
  editedMetadata
}) => {
  const changes = Object.entries(dirtyFields).reduce(
    (recordedChanges, [field]) => ({
      ...recordedChanges,
      [`${field}_change`]: JSON.stringify({
        from: originalMetadata[field],
        to: editedMetadata[field]
      })
    }),
    {}
  )
  DataLayer.trackItemEditSubmit({ itemId, changes })
}
export const analyticsViewSustainability = () => {
  DataLayer.trackViewsSustainability()
}

export const analyticsPersonalisedSearchToggle = ({ value }) => {
  DataLayer.trackPersonalisedSearchToggle({ value })
}

export const analyticsAddUserPreference = ({ category }) => {
  DataLayer.trackAddUserPreference({ category })
}

export const analyticsRemoveUserPreference = ({ category }) => {
  DataLayer.trackRemoveUserPreference({ category })
}

export const analyticsProductIdSearchClicked = ({ productId, itemId }) => {
  DataLayer.trackClickSearchProductID({ productId, itemId })
}

export const analyticsAppDownloadClick = (payload) => DataLayer.trackAppDownloadClick(payload)
